import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import debounce from "lodash.debounce";

import Checkbox from "components/Checkbox";
import { ChevronDownV3Icon } from "components/icons";
import Image from "components/Image";
import InputMultipleTags, { TInputTag } from "components/InputMultipleTag";
import Skeleton from "components/Skeleton";

import { useTranslationContext } from "context/TranslationContext";
import { useOutsideClick } from "hooks/useOutsideClick";
import cn from "lib/cn";

interface SelectMultipleTagProps {
  options?: TInputTag[];
  label?: string;
  placeHolder?: string;
  values?: TInputTag[];
  maxSelectedTag?: number;
  clearInputOnSelect?: boolean;
  withImage?: boolean;
  className?: string;
  labelClassName?: string;
  showIdValue?: boolean;
  loading?: boolean;
  onOptionsOpen?: () => void;
  setValues: Dispatch<SetStateAction<TInputTag[]>>;
  onInputChange?: (value: string) => void;
  onValuesChange?: (values: TInputTag[]) => void;
}

const SelectMultipleTag = ({
  options,
  label,
  placeHolder = "",
  values = [],
  maxSelectedTag,
  clearInputOnSelect,
  withImage,
  className,
  labelClassName,
  showIdValue = false,
  loading,
  onOptionsOpen,
  setValues,
  onInputChange,
}: SelectMultipleTagProps) => {
  const { t } = useTranslationContext();
  const refDiv = useRef(null);
  const [isOptionsDisplayed, setIsOptionsDisplayed] = useState(false);

  const handleSetTagValues = (tags: TInputTag[]) => {
    setValues(tags);
    if (clearInputOnSelect) {
      setInputvalue("");
    }
  };

  const [_inputKeyPress, setInputKeyPress] = useState(false);

  const [inputValue, setInputvalue] = useState("");

  const displayedOptions = inputValue
    ? options?.filter(({ name }) =>
        name.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase())
      )
    : options;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(
    debounce((value: string) => {
      if (value) {
        onInputChange?.(value);
        setInputKeyPress(false);
      } else {
        onInputChange?.("");
      }
    }, 500),
    []
  );

  const handleInputchange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debounceSearch(value);
    setInputvalue(value);
    setInputKeyPress(true);
  };

  const clearInputField = () => {
    setInputvalue("");
  };

  const handleFocusChange = (isOptionsDisplayed: boolean) => {
    if (isOptionsDisplayed) {
      onOptionsOpen?.();
      setIsOptionsDisplayed(isOptionsDisplayed);
    }
  };

  const handleClickOption = (tag: TInputTag) => {
    setValues((prevTag) => {
      const isSelected = prevTag.some((tagValue) => tagValue.id === tag.id);

      if (isSelected) {
        return prevTag.filter((tagValue) => tagValue.id !== tag.id);
      }
      if (maxSelectedTag && prevTag.length > maxSelectedTag - 1) {
        return prevTag;
      }

      return [...prevTag, tag];
    });
    if (clearInputOnSelect) {
      setInputvalue("");
    }
  };

  useOutsideClick(refDiv, () => {
    setIsOptionsDisplayed(false);
  });

  return (
    <div className={cn("relative", className)}>
      {label && (
        <span className={cn("ml-1 text-gray-500 text-base", labelClassName)}>
          {label}
        </span>
      )}
      {loading ? (
        <Skeleton className="w-full h-10 rounded-md" />
      ) : (
        <div ref={refDiv}>
          <InputMultipleTags
            values={values}
            onSetTagValues={handleSetTagValues}
            tagValue={inputValue}
            onChange={handleInputchange}
            keyDownActive={false}
            onClearInputField={clearInputField}
            loading={false}
            placeHolder={placeHolder}
            rightIcon={<ChevronDownV3Icon width="12" height="6" />}
            onFocusChange={handleFocusChange}
            showIdValue={showIdValue}
          />
        </div>
      )}
      <div className="relative pl-3 -ml-[0.45rem] w-auto">
        <div className="absolute w-[96.5%] top-13 drop-shadow-md bg-white rounded-md z-10 max-h-[11.25rem] overflow-auto">
          {isOptionsDisplayed &&
            displayedOptions?.map(({ id, name, image }) => {
              const isChecked = values.some((tagValue) => tagValue.id === id);
              return (
                <div
                  key={id}
                  className="flex gap-x-2 items-center border-b-[0.75px] border-gray-50 w-full hover:bg-gray-100 px-2 py-2"
                  onClick={() => handleClickOption({ id, name })}
                >
                  <Checkbox value={id} isChecked={isChecked} />
                  {withImage && (
                    <Image
                      imageUrl={image}
                      width={26}
                      height={26}
                      label={name}
                      className="text-md font-medium bg-gradient-to-t from-gradient3-500 to-gradient3-200/50 mx-1"
                      borderRadius="50%"
                    />
                  )}
                  {showIdValue ? t(id) : t(name)}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default SelectMultipleTag;
